import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { connect } from 'react-redux';
import TablePaginationAndButtons from './TablePaginationAndButtons';
import TableDropdownFilter from './TableDropdownFilter';
import { setTableRef } from '../../actions';
import { filterLogicStartsWith, getConfig, getParamFromUrl } from '../../utils/helpers';
import { dataPlaceholder } from '../../utils/constants';
import classNames from 'classnames';
import { stockwaveClassNames } from '../../utils/widgets/stockwave';

/** Props for DataTable.  All `show*` props have `default = false`
 * @typedef {{
 *  columns: Array<object|string>,
 *  customPageSizes?: number[],
 *  data: object[],
 *  loading: boolean,
 *  showCSVButton: boolean,
 *  showPrintButton: boolean,
 *  showWatchlistButton: boolean,
 *  showTransportButton: boolean,
 *  title: string
 * }} DataTableProps
 */

/**
 * @extends {React.Component<Partial<DataTableProps>>}
 */
class DataTable extends Component {
  state = {
    isStockwaveEnabled: getConfig('enableStockwave') === true,
  }

  componentDidMount() {
    // set this in store so 'withTable' has reference and can access the filtered/sorted data
    // and give it to CSV and Print buttons
    // not sure why, but some tables at some times do not get the ref
    this.props.setTableRef(this.reactTable);
  }

  // this is to decide if the row should be highlighted or not
  hasBeenSelected(row) {
    return false;
    // const { selectedData, allSelected } = this.context;

    // if (row && allSelected) {
    //   return true;
    // }

    // // offers pages use offerId because there can be multiple offers on same item
    // if (has(row, 'original.offerId')) {
    //   return (
    //     findIndex(selectedData, {
    //       offerId: row.original.offerId,
    //     }) > -1
    //   );
    // }

    // // all others are selected by itemId / id
    // if (has(row, 'original.itemId')) {
    //   return findIndex(selectedData, { itemId: row.original.itemId }) > -1;
    // }
    // if (has(row, 'original.id')) {
    //   return findIndex(selectedData, { id: row.original.id }) > -1;
    // }

    // // except buyer arbs
    // if (!has(row, 'original.itemId') && has(row, 'original.vin')) {
    //   return findIndex(selectedData, { vin: row.original.vin }) > -1;
    // }
    // if (!has(row, 'original.id') && has(row, 'original.vIN')) {
    //   return findIndex(selectedData, { vIN: row.original.vIN }) > -1;
    // }

    // return false;
  }

  getPageSizeOptions(data) {
    const len = data.length;
    let options = [20, 40, 60, 100];
    if (this.props.customPageSizes && this.props.customPageSizes.length) {
      options = this.props.customPageSizes;
    }
    options = [...options, len].sort((a, b) => (a < b ? -1 : 1));
    options = options.slice(0, options.indexOf(len) + 1);
    return options;
  }

  getDefaultPageSize() {
    const defaultPageSize = 20;
    const {customPageSizes=[]} = this.props;
    return (customPageSizes.length) 
      ? customPageSizes[0]
      : defaultPageSize;
  }

  customSortMethod = (a, b, desc) => {
    // dataPlaceholder always has lowest sort priority
    if (a === dataPlaceholder) return -1;

    // force null and undefined to the bottom
    a = a === null || a === undefined ? '' : a;
    b = b === null || b === undefined ? '' : b;

    // force any string values to lowercase
    a = typeof a === 'string' ? a.toLowerCase() : a;
    b = typeof b === 'string' ? b.toLowerCase() : b;

    // cut out all non-alphanumberic characters
    if (typeof a === 'string') a = a.replace(/\W+/g, '');
    if (typeof b === 'string') b = b.replace(/\W+/g, '');

    // function to tell if a string is *really* a number in disguise
    // (i could have used NaN but that is unreliable)
    const isNumberString = val => {
      if (typeof val !== 'string') return false;
      return !/[a-zA-Z]/g.test(val);
    };

    // if it *is* a number make it a number so you you can compare by value not alphabetically
    if (isNumberString(a)) a = Number(a);
    if (isNumberString(b)) b = Number(b);

    // if comparing mixed values numbers always have lower sort priority
    if (typeof a === 'string' && typeof b === 'number') return 1;
    if (typeof b === 'string' && typeof a === 'number') return -1;

    // Return either 1 or -1 to indicate a sort priority
    if (a > b) return 1;
    if (a < b) return -1;

    // returning 0, undefined or any falsey value will use subsequent sorts or
    // the index as a tiebreaker
    return 0;
  };

  render() {
    if (!this.props.loading && this.props.data && !this.props.data.length) {
      return (
        <div style={{ margin: 20, fontSize: 16 }}>
          There are currently no items
        </div>
      );
    }
    return (
      <ReactTable
        ref={node => (this.reactTable = node)}
        className="react-table -striped -highlight"
        columns={this.props.columns}
        data={this.props.data}
        dataForButtonActions={
          // aka smart data
          this.context.selectedData.length // if the user selects rows use those
          ? this.context.selectedData
          : this.context.currentData.length // else if the user has data filtered use the filtered data
          ? this.context.currentData
          : this.props.data // else use all the data
        }
        defaultFilterMethod={filterLogicStartsWith}
        defaultSorted={[ // sort priority -- lot# > stock# > cr grade
          {id: 'lotNumber', desc: false},
          {id: 'stockNumber',desc: true},
          {id: 'crGrade', desc: true}
        ]}
        defaultSortMethod={this.customSortMethod}
        defaultPageSize={this.getDefaultPageSize()}
        filterable={true}
        filtered={this.context.filtered}
        FilterComponent={props => (
          // if filterable, this is the default filter component
          <TableDropdownFilter
            filter={props.filter}
            column={props.column}
            onChange={props.onChange}
            data={
              this.context.currentData?.length // else if the user has data filtered use the filtered data
              ? this.context.currentData
              : this.props.data // else use all the data
            }
          />
        )}
        getProps={() => ({ style: styles.bodyStyle })}
        getTableProps={() => ({ style: styles.tableStyle })}
        getTheadProps={() => ({ style: styles.headerRowStyle })}
        getTheadThProps={() => ({ style: styles.headerCellStyle })}
        // Disabled (2025.11): Prevents dynamic row height if no filters
        // getTheadFilterTrProps={() => ({ style: styles.filterRowStyle })}
        getTheadFilterThProps={() => ({ style: styles.filterCellStyle })}
        getTrProps={(state, row, column) =>
          this.hasBeenSelected(row)
            ? {
                style: { ...styles.rowStyle, backgroundColor: '#ccc' },
              }
            : { style: styles.rowStyle }
        }
        getTdProps={(state, row, column) =>
          column.id === 'checkbox' || column.id === 'image'
            ? {
                style: {
                  ...styles.cellStyle,
                  padding: 0,
                  textOverflow: 'initial',
                },
              }
            : { style: styles.cellStyle }
        }
        loading={this.props.loading}
        onSortedChange={this.context.updateCurrentData} // for the CSV and Print buttons to have filtered/sorted data
        onFilteredChange={this.context.updateCurrentData} // for the CSV and Print buttons to have filtered/sorted data
        pageSizeOptions={this.getPageSizeOptions(this.props.data)}
        PaginationComponent={props => {
          const pageParam = getParamFromUrl('p');
          const manualPage = pageParam ? Number(pageParam - 1) : null;
          return (
            <TablePaginationAndButtons
              {...props}
              page={manualPage || props.page}
              showCSVButton={this.props.showCSVButton}
              showPrintButton={this.props.showPrintButton}
              showWatchlistButton={this.props.showWatchlistButton}
            />
          );
        }}
        resizable={false}
        selectedData={this.props.selectedData}
        showPaginationTop={true}
        showPaginationBottom={true}
        sorted={this.context.sorted?.id ? [this.context.sorted] : []}
        title={this.props.title}
        TrGroupComponent={({ children, className, ...rest }) => { 
          // console.info('DataTable TrGroupComponent', { children, className, rest });
          return (
            <div 
              className={classNames('rt-tr-group', className, stockwaveClassNames.highlight, stockwaveClassNames.srpVehicleRow)} 
              role="rowgroup" 
              {...rest}
            >
              {children}
            </div>
        )}}
      />
    );
  }
}

const styles = {
  bodyStyle: {
    border: 'none',
  },
  tableStyle: {
    borderBottom: '1px solid gray',
    overflow: 'auto hidden',
  },
  headerRowStyle: {
    borderTop: '1px solid gray',
    boxShadow: 'none',
    dropShadow: 'none',
    overflow: 'hidden',
  },
  headerCellStyle: {
    fontWeight: 'bold',
    outline: 'none',
    overflow: 'hidden',
    paddingBottom: 2,
    paddingLeft: 5,
    paddingRight: 12,
    paddingTop: 5,
    textAlign: 'center',
    textOverflow: 'ellipses',
    whiteSpace: 'normal',
    wordBreak: 'normal',
  },
  filterRowStyle: {
    height: 42,
    borderBottom: '1px solid gray',
  },
  filterCellStyle: {
    backgroundImage: 'none',
    fontWeight: 'bold',
    outline: 'none',
    padding: '5px 2px 12px 5px',
    textAlign: 'center',
    whiteSpace: 'pre-line',
  },
  rowStyle: {},
  cellStyle: {
    maxHeight: 150,
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipses',
    whiteSpace: 'normal',
  },
};

DataTable.defaultProps = {
  showCSVButton: false,
  showPrintButton: false,
  showWatchlistButton: false,
  showTransportButton: false
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  showCSVButton: PropTypes.bool,
  showPrintButton: PropTypes.bool,
  showWatchlistButton: PropTypes.bool,
  showTransportButton: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

DataTable.contextTypes = {
  allSelected: PropTypes.bool,
  selectedData: PropTypes.array,
  currentData: PropTypes.array,
  updateCurrentData: PropTypes.func,
  filtered: PropTypes.array, // [ {id: '', value: ''} ]
  sorted: PropTypes.object, // {id: '', desc: boolean}
};

export default connect(null, { setTableRef })(DataTable);
